/**
 * Dedicated logging utility for tracking optimistic subtotal calculations and overwrites
 * Helps debug timing issues and identify sources of incorrect state updates
 */

export interface OptimisticLogEntry {
  timestamp: string;
  source: string;
  action: string;
  beforeValue?: number;
  afterValue?: number;
  optimisticState?: any;
  cartState?: any;
  stackTrace?: string;
  additionalData?: any;
}

class OptimisticLogger {
  private logs: OptimisticLogEntry[] = [];
  private enabled = true; // Set to false to disable logging

  log(entry: Partial<OptimisticLogEntry>) {
    if (!this.enabled) return;

    const timestamp = new Date().toISOString();
    const stackTrace = this.getStackTrace();

    const logEntry: OptimisticLogEntry = {
      timestamp,
      source: entry.source || "unknown",
      action: entry.action || "unknown",
      stackTrace,
      ...entry,
    };

    this.logs.push(logEntry);

    // Console log with color coding for easy identification
    const color = this.getColorForSource(entry.source);
    console.group(
      `%c🔄 OPTIMISTIC LOG: ${entry.source} - ${entry.action}`,
      `color: ${color}; font-weight: bold;`
    );
    console.log("📅 Time:", timestamp);

    if (entry.beforeValue !== undefined && entry.afterValue !== undefined) {
      console.log(
        `💰 Subtotal: $${entry.beforeValue.toFixed(
          2
        )} → $${entry.afterValue.toFixed(2)}`
      );
      console.log(
        `📊 Change: ${entry.afterValue > entry.beforeValue ? "+" : ""}$${(
          entry.afterValue - entry.beforeValue
        ).toFixed(2)}`
      );
    } else if (entry.afterValue !== undefined) {
      console.log(`💰 Subtotal: $${entry.afterValue.toFixed(2)}`);
    }

    if (entry.optimisticState) {
      console.log("🎯 Optimistic State:", entry.optimisticState);
    }

    if (entry.cartState) {
      console.log("🛒 Cart State:", entry.cartState);
    }

    if (entry.additionalData) {
      console.log("📋 Additional Data:", entry.additionalData);
    }

    console.log("📍 Stack Trace:", stackTrace);
    console.groupEnd();

    // Keep only last 100 logs to prevent memory issues
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }
  }

  private getColorForSource(source?: string): string {
    const colors = {
      "MiniCart.optimisticCalculation": "#2196F3", // Blue
      "BottomActions.subtotalDisplay": "#4CAF50", // Green
      useDebounceCartUpdates: "#FF9800", // Orange
      cartReducer: "#9C27B0", // Purple
      "API.response": "#F44336", // Red
      "API.addItem": "#E91E63", // Pink
      "API.updateItem": "#3F51B5", // Indigo
      "API.removeItem": "#795548", // Brown
      "Redux.stateChange": "#607D8B", // Blue Grey
      "Component.render": "#009688", // Teal
    };
    return colors[source as keyof typeof colors] || "#666666";
  }

  private getStackTrace(): string {
    try {
      throw new Error();
    } catch (e) {
      return (
        (e as Error).stack?.split("\n").slice(2, 6).join("\n") ||
        "No stack trace available"
      );
    }
  }

  // Method to log subtotal changes specifically
  logSubtotalChange(
    source: string,
    beforeValue: number,
    afterValue: number,
    optimisticState?: any,
    additionalData?: any
  ) {
    this.log({
      source,
      action: "subtotalChange",
      beforeValue,
      afterValue,
      optimisticState,
      additionalData,
    });
  }

  // Method to log optimistic state changes
  logOptimisticStateChange(
    source: string,
    action: string,
    state: any,
    additionalData?: any
  ) {
    this.log({
      source,
      action,
      optimisticState: state,
      additionalData,
    });
  }

  // Method to log API responses
  logApiResponse(endpoint: string, response: any, cartState?: any) {
    this.log({
      source: `API.${endpoint}`,
      action: "response",
      cartState,
      additionalData: response,
    });
  }

  // Method to get all logs (for debugging)
  getAllLogs(): OptimisticLogEntry[] {
    return [...this.logs];
  }

  // Method to get recent logs
  getRecentLogs(count: number = 10): OptimisticLogEntry[] {
    return this.logs.slice(-count);
  }

  // Method to clear logs
  clearLogs() {
    this.logs = [];
    console.log("🧹 Optimistic logs cleared");
  }

  // Method to export logs as JSON (for sharing with development team)
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Method to enable/disable logging
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    console.log(`🔄 Optimistic logging ${enabled ? "enabled" : "disabled"}`);
  }
}

// Create singleton instance
export const optimisticLogger = new OptimisticLogger();

// Export convenience methods for easy use
export const logSubtotalChange =
  optimisticLogger.logSubtotalChange.bind(optimisticLogger);
export const logOptimisticStateChange =
  optimisticLogger.logOptimisticStateChange.bind(optimisticLogger);
export const logApiResponse =
  optimisticLogger.logApiResponse.bind(optimisticLogger);

// Global methods for debugging in console
(window as any).optimisticLogger = optimisticLogger;
(window as any).clearOptimisticLogs = () => optimisticLogger.clearLogs();
(window as any).getOptimisticLogs = () => optimisticLogger.getAllLogs();
(window as any).exportOptimisticLogs = () => optimisticLogger.exportLogs();

export default optimisticLogger;
